import { Divider, Stack } from 'component-library';
import { FC, HTMLAttributes, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { Threat, TrendLabel as TrendLabelEnum } from '@/api/rest/resources/conservation';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { ChartLegendBar } from '@/components/Charts';
import {
  ChartTooltip,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from '@/components/Charts/components/Tooltip';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { ChartProps } from '@/components/Charts/types';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import {
  Bar,
  Chart,
  ComposedChart,
  TooltipProps,
  useTimeseriesXAxisProps,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/Chart';
import {
  RenderTrendLine,
  TrendLabel,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/ReferenceLine';
import { colorPalette } from '@/theme/colorPalette';
import { isNullish } from '@/utils/booleans';
import { printYear } from '@/utils/formatting/date';

export const INTACTNESS_KEY = 'no_degradation_area';
export const SUM_OF_THREATS = 'sumOfThreats';

export const ORDERED_THREATS = {
  1: Threat.built,
  2: Threat.mine,
  3: Threat.overgrazing,
  4: Threat.treeloss,
  5: Threat.road,
  6: Threat.minor_road,
  7: Threat.major_road,
  8: Threat.airstripes,
  9: Threat.crop,
  10: Threat.fire,
  11: Threat.edge,
  12: Threat.river,
  13: Threat.other,
};
export const THREATS: Threat[] = Object.values(ORDERED_THREATS).concat(
  Object.keys(Threat)
    .map((key) => key as Threat)
    .filter((threat) => !Object.values(ORDERED_THREATS).includes(threat)),
);
const UNIT = UnitEnum['%'];

const chartColors = {
  [Threat.built]: { fill: '#B28B6F' },
  [Threat.treeloss]: { fill: '#ADA476' },
  [Threat.crop]: { fill: '#CCBC6E' },
  [Threat.road]: { fill: '#91A3B5' },
  [Threat.mine]: { fill: '#CC8D70' },
  [Threat.overgrazing]: { fill: '#E5A067' },
  [Threat.other]: { fill: '#A69BCC' },
  [Threat.minor_road]: { fill: '#F48FB1' },
  [Threat.major_road]: { fill: '#AD1457' },
  [Threat.airstripes]: { fill: '#3A006A' },
  [Threat.fire]: { fill: '#880808' },
  [Threat.edge]: { fill: '#ffd000' },
  [Threat.river]: { fill: '#38AFCD' },
  [INTACTNESS_KEY]: {
    fill: '#A2BD75',
  },
  [SUM_OF_THREATS]: {
    stroke: '#004C3D',
    dot: '#000000',
  },
} as const;

const SHARED_BAR_PROPS = {
  stackId: 'degradationDrivers',
  isAnimationActive: false,
  barSize: 21,
  stroke: '#fff',
  strokeWidth: 1,
};

export type ThreatsTimeSeries = { [Key in Threat]: number | null };

export type DDChartData = ThreatsTimeSeries & {
  date: Date;
};

type DegradationDriversChart = HTMLAttributes<HTMLDivElement> & {
  data: DDChartData[];
  trendLabel: TrendLabelEnum | null;
} & Omit<ChartProps, 'data'>;

export const DegradationDriversHistoricalChart: FC<DegradationDriversChart> = ({ data, trendLabel, ...delegated }) => {
  const xAxisProps = useTimeseriesXAxisProps(data);
  const chartConfig = useGetChartElementsConfig();
  const { t } = useTranslation();

  const threatsWithData = getThreatsWithData(data);

  const dataWithIntactness = data.map((dataSet) => {
    const sumOfThreats = Object.entries(dataSet).reduce((acc: number, [key, value]) => {
      if (THREATS.includes(key as Threat) && typeof value === 'number') {
        return acc + value;
      }

      return acc;
    }, 0);

    return {
      [SUM_OF_THREATS]: sumOfThreats,
      ...dataSet,
    };
  });

  return (
    <>
      <Chart.Container
        {...delegated}
        data={dataWithIntactness}
        config={chartConfig}
        data-testid='degradation-drivers-historic-chart'
      >
        <ComposedChart data={dataWithIntactness}>
          {threatsWithData.map((threat) => (
            <Bar
              key={threat}
              fill={chartColors[`${threat}`].fill}
              dataKey={threat}
              className={`${threat}-bar`}
              activeBar={<CustomActiveBar fill={chartColors[`${threat}`].fill} />}
              {...SHARED_BAR_PROPS}
            />
          ))}
          <Bar
            key={INTACTNESS_KEY}
            fill={chartColors[`${INTACTNESS_KEY}`].fill}
            dataKey={INTACTNESS_KEY}
            className={`${INTACTNESS_KEY}-bar`}
            activeBar={<CustomActiveBar fill={chartColors[`${INTACTNESS_KEY}`].fill} />}
            {...SHARED_BAR_PROPS}
          />
          {RenderTrendLine({
            dataKey: SUM_OF_THREATS,
            stroke: chartColors[`${SUM_OF_THREATS}`].stroke,
            label: (props) =>
              props.index === data.length - 1 && trendLabel ? (
                <TrendLabel
                  trend={`${trendLabel}`}
                  lineColor={chartColors[`${SUM_OF_THREATS}`].stroke}
                  strokeWidth={2}
                  infoPopoverProps={{
                    title: t(`shared.projects.project.conservation.trendLabel.${trendLabel}`),
                    body: t(
                      `shared.ncaDetail.habitatIntactness.degradationDriversHistoric.explainers.trendLabel.${trendLabel}`,
                    ),
                  }}
                  xOffset={40}
                  {...props}
                />
              ) : (
                <></>
              ),
            dotFill: chartColors[`${SUM_OF_THREATS}`].dot,
          })}
          <Chart.XAxis {...xAxisProps} padding={{ left: 40, right: 100 }} className='dd-x-axis' />
          <Chart.YAxis domain={[0, 100]} className='dd-y-axis'>
            <Chart.YAxisLabel>{UNIT}</Chart.YAxisLabel>
          </Chart.YAxis>
          <Chart.Tooltip content={<CustomTooltip />} cursor={false} />
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar threatsWithData={threatsWithData} />
    </>
  );
};

export const CustomActiveBar = ({ x, y, width, height, fill }: SVGProps<SVGElement>) => {
  if (!x || !y || !width || !height) return <></>;

  const barMiddleX = (x as number) + (width as number) / 2;
  const barBottom = (y as number) + (height as number);

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke={SHARED_BAR_PROPS.stroke} />
      <line x1={barMiddleX} x2={barMiddleX} y1={y} y2={barBottom} stroke={colorPalette.divider} strokeWidth={1} />
    </g>
  );
};

export const getThreatsWithData = (data: DDChartData[]): Threat[] => {
  return THREATS.reduce((threats: Threat[], threat) => {
    const hasThreatData = data.some((dataSet) => dataSet[`${threat}`] !== null);

    if (hasThreatData) {
      threats.push(threat);
    }

    return threats;
  }, []);
};

const useGetChartElementsConfig = () => {
  const { t } = useTranslation();

  const threatsConfig = Object.fromEntries(
    THREATS.map((threat) => [
      threat,
      {
        label: t(`shared.projects.project.conservation.labels.${threat}`),
        color: chartColors[`${threat}`].fill,
      },
    ]),
  );

  return {
    [INTACTNESS_KEY]: {
      label: t('shared.ncaDetail.ecologicalIntegrity.breakdown.habitatIntactness.attributeLabel.intact'),
      color: chartColors[`${INTACTNESS_KEY}`].fill,
    },
    ...threatsConfig,
  };
};

export const CustomLegendBar = ({ threatsWithData }: { threatsWithData: Threat[] }) => {
  const chartElementsConfig = useGetChartElementsConfig();

  const legendBarItems: ChartLegendBarItem[] = Object.entries(chartElementsConfig)
    .filter(([key]) => key === INTACTNESS_KEY || threatsWithData.includes(key as Threat))
    .map(([, threatConfig]) => ({
      ...threatConfig,
      shape: 'column',
    }));

  return (
    <ChartLegendBar
      className='flex-wrap'
      items={legendBarItems}
      data-testid='degradation-drivers-historic-chart-legend'
    />
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  const displayThreats = Object.fromEntries(
    THREATS.map((threat) => {
      const threatData = payload?.find(({ dataKey }) => dataKey === threat);

      const threatDisplayValue =
        isNullish(threatData?.value) || threatData?.value === 0
          ? null
          : `${getDisplayNumber(threatData?.value as string, window.navigator.language)} ${UNIT}`;

      return [threat, threatDisplayValue];
    }),
  );

  const intactnessData = payload?.find(({ dataKey }) => dataKey === INTACTNESS_KEY);
  const intactnessDisplay = !isNullish(intactnessData?.value)
    ? `${getDisplayNumber(intactnessData?.value as string, window.navigator.language)} ${UNIT}`
    : t('global.analysis.noData');

  const sumOfThreatsData = payload?.at(0)?.payload[`${SUM_OF_THREATS}`]; // We use the first payload as the sum of threats is the same for all
  const sumOfThreatsDisplay = !isNullish(sumOfThreatsData)
    ? `${getDisplayNumber(sumOfThreatsData as string, window.navigator.language)} ${UNIT}`
    : t('global.analysis.noData');

  if (!active) return null;

  return (
    <ChartTooltip className='ml-1'>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
      </Stack>
      <Stack spacing={2} direction='col'>
        <Stack direction='row' spacing={8} className='justify-between'>
          <ChartTooltipTextPrimary>
            {t('shared.ncaDetail.habitatIntactness.degradationDriversHistoric.statsHeading')}
          </ChartTooltipTextPrimary>
          <ChartTooltipTextPrimary>{sumOfThreatsDisplay}</ChartTooltipTextPrimary>
        </Stack>
        <Divider />
        {THREATS.map((threat) => {
          const threatValue = displayThreats[`${threat}`];

          if (!threatValue) return null;

          return (
            <div key={threat}>
              <Stack direction='row' spacing={8} className='justify-between'>
                <ChartTooltipTextSecondary>
                  {t(`shared.projects.project.conservation.labels.${threat}`)}
                </ChartTooltipTextSecondary>
                <ChartTooltipTextSecondary>{threatValue}</ChartTooltipTextSecondary>
              </Stack>
              <Divider className='mt-2' />
            </div>
          );
        })}
        <Stack direction='row' spacing={8} className='justify-between'>
          <ChartTooltipTextPrimary>
            {t('shared.ncaDetail.habitatIntactness.degradationDriversHistoric.habitatIntactness')}
          </ChartTooltipTextPrimary>
          <ChartTooltipTextPrimary>{intactnessDisplay}</ChartTooltipTextPrimary>
        </Stack>
      </Stack>
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipDegradationDriversHistorical };
